import { useEffect, useState, useRef } from 'react'
import { useApi } from '../lib/api'
import { useCurrentUser } from '../lib/auth'
import type { User, Role } from '../types'

const ALL_ROLES: { value: Role; label: string }[] = [
  { value: 'admin',       label: 'Administrator' },
  { value: 'sales',       label: 'Vertrieb' },
  { value: 'fulfillment', label: 'Auftragsabwicklung' },
  { value: 'accounting',  label: 'Buchhaltung' },
  { value: 'design',      label: 'Design' },
]

// ---------------------------------------------------------------------------
// Inline form for create / edit
// ---------------------------------------------------------------------------
interface UserFormProps {
  initial?: Partial<User>
  onSave: (u: User) => void
  onCancel: () => void
}

function UserForm({ initial, onSave, onCancel }: UserFormProps) {
  const { apiFetch } = useApi()
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [form, setForm] = useState({
    name: initial?.name ?? '',
    email: initial?.email ?? '',
    roles: initial?.roles ?? [] as Role[],
  })

  function toggleRole(role: Role) {
    setForm((f) => ({
      ...f,
      roles: f.roles.includes(role)
        ? f.roles.filter((r) => r !== role)
        : [...f.roles, role],
    }))
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setSaving(true)
    try {
      const url = initial?.id ? `/api/users/${initial.id}` : '/api/users'
      const method = initial?.id ? 'PUT' : 'POST'
      const saved = await apiFetch<User>(url, { method, body: JSON.stringify(form) })
      onSave(saved)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler beim Speichern')
    } finally {
      setSaving(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div className="form-group" style={{ marginBottom: 0 }}>
        <label>Name *</label>
        <input required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Max Mustermann" />
      </div>
      <div className="form-group" style={{ marginBottom: 0 }}>
        <label>E-Mail *</label>
        <input required type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="max@beispiel.de" />
      </div>
      <div className="form-group" style={{ marginBottom: 0 }}>
        <label>Rollen</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
          {ALL_ROLES.map(({ value, label }) => (
            <label key={value} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={form.roles.includes(value)}
                onChange={() => toggleRole(value)}
              />
              {label}
            </label>
          ))}
        </div>
      </div>
      {error && <div className="error-msg">{error}</div>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Wird gespeichert…' : 'Speichern'}</button>
        <button type="button" className="btn btn-secondary" onClick={onCancel}>Abbrechen</button>
      </div>
    </form>
  )
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function UsersPage() {
  const { apiFetch } = useApi()
  const { user: currentUser } = useCurrentUser()
  const [users, setUsers] = useState<User[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [showForm, setShowForm] = useState(false)
  const [editing, setEditing] = useState<User | null>(null)
  const [deleting, setDeleting] = useState<number | null>(null)
  const PAGE_SIZE = 25
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)

  function load(q: string, p: number) {
    setLoading(true)
    setLoadError(null)
    const params = new URLSearchParams({
      limit: String(PAGE_SIZE),
      offset: String((p - 1) * PAGE_SIZE),
    })
    if (q) params.set('q', q)
    apiFetch<{ users: User[]; total: number }>(`/api/users?${params}`)
      .then(({ users: u, total: t }) => { setUsers(u); setTotal(t) })
      .catch((e: unknown) => setLoadError(e instanceof Error ? e.message : 'Fehler beim Laden der Benutzer'))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    load(search, page)
  }, [page]) // eslint-disable-line react-hooks/exhaustive-deps

  function handleSearchChange(val: string) {
    setSearch(val)
    setPage(1)
    if (searchTimeout.current) clearTimeout(searchTimeout.current)
    searchTimeout.current = setTimeout(() => load(val, 1), 300)
  }

  function handleSaved(u: User) {
    if (editing) {
      setUsers((prev) => prev.map((x) => (x.id === u.id ? u : x)))
    } else {
      load(search, page)
    }
    setShowForm(false)
    setEditing(null)
  }

  async function handleDelete(id: number) {
    if (!confirm('Diesen Benutzer löschen?')) return
    setDeleting(id)
    try {
      await apiFetch(`/api/users/${id}`, { method: 'DELETE' })
      setUsers((prev) => prev.filter((u) => u.id !== id))
      setTotal((t) => t - 1)
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Fehler beim Löschen')
    } finally {
      setDeleting(null)
    }
  }

  const totalPages = Math.ceil(total / PAGE_SIZE)

  const ROLE_LABELS: Record<Role, string> = {
    admin: 'Administrator',
    sales: 'Vertrieb',
    fulfillment: 'Auftragsabwicklung',
    accounting: 'Buchhaltung',
    design: 'Design',
  }

  if (!currentUser?.roles.includes('admin')) {
    return <div style={{ padding: 32, color: 'var(--color-muted)' }}>Kein Zugriff – nur für Administratoren.</div>
  }

  return (
    <div>
      <h1 style={{ marginBottom: 20 }}>Benutzer</h1>

      {/* Filter bar */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center' }}>
        <input
          type="search"
          placeholder="Name oder E-Mail suchen…"
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
          style={{ width: 320 }}
        />
        <button
          className="btn btn-primary"
          onClick={() => { setEditing(null); setShowForm(true) }}
        >
          + Neuer Benutzer
        </button>
      </div>

      {/* Inline create form */}
      {showForm && !editing && (
        <div className="card" style={{ marginBottom: 16 }}>
          <h3 style={{ marginTop: 0, marginBottom: 16 }}>Neuer Benutzer</h3>
          <UserForm onSave={handleSaved} onCancel={() => setShowForm(false)} />
        </div>
      )}

      {loadError && (
        <div className="error-msg" style={{ marginBottom: 16 }}>
          Fehler beim Laden der Benutzer: {loadError}
        </div>
      )}

      <div className="card" style={{ padding: 0 }}>
        {loading ? (
          <div className="loading">Benutzer werden geladen…</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>E-Mail</th>
                <th>Rollen</th>
                <th style={{ width: 120 }}></th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 && (
                <tr><td colSpan={4} style={{ textAlign: 'center', color: 'var(--color-muted)' }}>Keine Benutzer gefunden</td></tr>
              )}
              {users.map((u) => (
                <tr key={u.id}>
                  {editing?.id === u.id ? (
                    <td colSpan={4}>
                      <UserForm
                        initial={editing}
                        onSave={handleSaved}
                        onCancel={() => setEditing(null)}
                      />
                    </td>
                  ) : (
                    <>
                      <td style={{ fontWeight: 500 }}>{u.name}</td>
                      <td>{u.email}</td>
                      <td>
                        {u.roles.length === 0
                          ? <span style={{ color: 'var(--color-muted)' }}>—</span>
                          : u.roles.map((r) => ROLE_LABELS[r] ?? r).join(', ')
                        }
                      </td>
                      <td style={{ display: 'flex', gap: 6 }}>
                        <button
                          className="btn btn-secondary"
                          style={{ padding: '4px 10px', fontSize: 12 }}
                          onClick={() => { setShowForm(false); setEditing(u) }}
                        >
                          Bearbeiten
                        </button>
                        <button
                          className="btn btn-secondary"
                          style={{ padding: '4px 10px', fontSize: 12, color: 'var(--color-danger, #ef4444)' }}
                          disabled={deleting === u.id || u.id === currentUser?.id}
                          title={u.id === currentUser?.id ? 'Eigenes Konto kann nicht gelöscht werden' : undefined}
                          onClick={() => handleDelete(u.id)}
                        >
                          {deleting === u.id ? '…' : 'Löschen'}
                        </button>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', gap: 8, marginTop: 16, alignItems: 'center' }}>
          <button className="btn btn-secondary" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>← Zurück</button>
          <span style={{ fontSize: 13, color: 'var(--color-muted)' }}>Seite {page} / {totalPages}</span>
          <button className="btn btn-secondary" disabled={page === totalPages} onClick={() => setPage((p) => p + 1)}>Weiter →</button>
        </div>
      )}
    </div>
  )
}
